import { useState, useEffect, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CheckCircle2, AlertCircle, Zap, Copy, Eye } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Link } from "react-router-dom";

interface ProcessedRecord {
  id: string;
  document_number: string;
  seller_name: string;
  seller_address: string;
  confidence: number;
}

export function QuickCaptureMode() {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedRecords, setProcessedRecords] = useState<ProcessedRecord[]>([]);
  const [isPasteReady, setIsPasteReady] = useState(true);
  const [selectedCounty, setSelectedCounty] = useState("randall");
  const pasteZoneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Focus the paste zone on mount
    pasteZoneRef.current?.focus();

    // Handle paste events globally when this component is visible
    const handlePaste = async (e: ClipboardEvent) => {
      if (isProcessing) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          e.preventDefault();
          const blob = items[i].getAsFile();
          if (blob) {
            await processImage(blob);
          }
          return;
        }
      }
    };

    // Handle drop events
    const handleDrop = async (e: DragEvent) => {
      e.preventDefault();
      if (isProcessing) return;

      const files = e.dataTransfer?.files;
      if (!files || files.length === 0) return;

      const file = files[0];
      if (file.type.startsWith('image/')) {
        await processImage(file);
      } else {
        toast.error("Please drop an image file");
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    document.addEventListener('paste', handlePaste);
    document.addEventListener('drop', handleDrop);
    document.addEventListener('dragover', handleDragOver);

    return () => {
      document.removeEventListener('paste', handlePaste);
      document.removeEventListener('drop', handleDrop);
      document.removeEventListener('dragover', handleDragOver);
    };
  }, [isProcessing]);

  const processImage = async (file: File | Blob) => {
    setIsProcessing(true);
    setIsPasteReady(false);

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onload = async (event) => {
        const base64Data = event.target?.result as string;

        toast.info("Processing with AI...", { duration: 2000 });

        const { data, error } = await supabase.functions.invoke('extract-dickinson-contract', {
          body: {
            imageData: base64Data,
            county: selectedCounty,
            metadata: {
              documentNumber: `AUTO-${Date.now()}`,
              documentType: 'Land Contract'
            }
          }
        });

        if (error) throw error;

        if (data.success) {
          const record: ProcessedRecord = {
            id: data.record.id,
            document_number: data.record.document_number,
            seller_name: data.record.grantor_name,
            seller_address: data.record.grantee_address,
            confidence: data.extractedData?.confidence || 0
          };

          setProcessedRecords(prev => [record, ...prev]);
          
          toast.success("✓ Extracted! Ready for next screenshot", {
            duration: 2000
          });

          // Play success sound (optional)
          const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBDGH0fDBiTQHF2Ys6+OQSA0PNG'); 
          audio.volume = 0.3;
          audio.play().catch(() => {});
        } else {
          toast.error(data.error || "Failed to process");
        }
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Processing error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to process image");
    } finally {
      setIsProcessing(false);
      // Re-enable paste after a short delay
      setTimeout(() => setIsPasteReady(true), 1000);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 border-2 border-primary/20">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <Zap className="h-8 w-8 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-2xl font-bold">⚡ Quick Capture Mode</h2>
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">County:</label>
                <Select value={selectedCounty} onValueChange={setSelectedCounty}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="randall">Randall County, TX</SelectItem>
                    <SelectItem value="dickinson">Dickinson County, KS</SelectItem>
                    <SelectItem value="williamson">Williamson County, TX</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-muted-foreground mb-4">
              Rapid-fire screenshot processing. Take a screenshot and paste it here instantly!
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="bg-background/50 backdrop-blur rounded-lg p-3 border">
                <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Copy className="h-4 w-4" />
                  Windows
                </h3>
                <ol className="text-sm space-y-1 text-muted-foreground">
                  <li>1. Open document in county site</li>
                  <li>2. Press <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Win + Shift + S</kbd></li>
                  <li>3. Select area to capture</li>
                  <li>4. Click here and press <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Ctrl + V</kbd></li>
                </ol>
              </div>

              <div className="bg-background/50 backdrop-blur rounded-lg p-3 border">
                <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                  <Copy className="h-4 w-4" />
                  Mac
                </h3>
                <ol className="text-sm space-y-1 text-muted-foreground">
                  <li>1. Open document in county site</li>
                  <li>2. Press <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Cmd + Shift + 4</kbd></li>
                  <li>3. Select area (auto-copies)</li>
                  <li>4. Click here and press <kbd className="px-2 py-1 bg-muted rounded text-xs font-mono">Cmd + V</kbd></li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Paste Zone */}
        <div
          ref={pasteZoneRef}
          tabIndex={0}
          className={`
            mt-4 min-h-[200px] border-4 border-dashed rounded-lg 
            flex flex-col items-center justify-center gap-3 p-8
            transition-all duration-200 cursor-pointer
            ${isProcessing 
              ? 'border-yellow-500 bg-yellow-50 dark:bg-yellow-950/20' 
              : isPasteReady 
                ? 'border-primary bg-primary/5 hover:bg-primary/10 hover:border-primary/60' 
                : 'border-green-500 bg-green-50 dark:bg-green-950/20'
            }
          `}
          onClick={() => pasteZoneRef.current?.focus()}
        >
          {isProcessing ? (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-yellow-600" />
              <p className="text-lg font-semibold text-yellow-900 dark:text-yellow-100">
                Processing with AI...
              </p>
            </>
          ) : isPasteReady ? (
            <>
              <div className="text-6xl mb-2">📋</div>
              <p className="text-xl font-bold text-primary">
                Paste Screenshot Here (Ctrl+V / Cmd+V)
              </p>
              <p className="text-sm text-muted-foreground">
                Or drag and drop an image file
              </p>
            </>
          ) : (
            <>
              <CheckCircle2 className="h-12 w-12 text-green-600" />
              <p className="text-lg font-semibold text-green-900 dark:text-green-100">
                ✓ Saved! Ready for next screenshot
              </p>
            </>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
          <Badge variant="secondary" className="text-sm">
            {processedRecords.length} processed this session
          </Badge>
          <div className="flex gap-2">
            <Button
              variant="default"
              size="sm"
              asChild
            >
              <a href="#results-tab">
                <Eye className="mr-2 h-4 w-4" />
                View All Extracted Records
              </a>
            </Button>
            {processedRecords.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setProcessedRecords([])}
              >
                Clear Session
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Processed Records */}
      {processedRecords.length > 0 && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            Processed This Session ({processedRecords.length})
          </h3>
          
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {processedRecords.map((record, index) => (
              <div 
                key={record.id}
                className={`p-4 rounded-lg border transition-all ${
                  index === 0 
                    ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' 
                    : 'bg-muted/50'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">
                        {record.document_number}
                      </Badge>
                      {record.confidence >= 80 ? (
                        <Badge className="bg-green-600 text-xs">
                          {record.confidence}% confidence
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-xs flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {record.confidence}% - Review needed
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium truncate">
                      <strong>Seller:</strong> {record.seller_name}
                    </p>
                    <p className="text-sm text-muted-foreground truncate">
                      <strong>Address:</strong> {record.seller_address || 'Not extracted'}
                    </p>
                  </div>
                  {index === 0 && (
                    <Badge className="bg-primary text-xs flex-shrink-0">
                      Latest
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-6 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
        <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-3">
          💡 Pro Tips for Speed
        </h3>
        <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0">•</span>
            <span>Keep this window side-by-side with the county records site</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0">•</span>
            <span>Use keyboard shortcuts to capture without using mouse</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0">•</span>
            <span>The paste zone auto-focuses so you can paste immediately</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0">•</span>
            <span>Watch for the green "Ready for next" message to pace yourself</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="flex-shrink-0">•</span>
            <span>Records with low confidence are flagged automatically for review</span>
          </li>
        </ul>
      </Card>
    </div>
  );
}
